import { useForm } from '@tanstack/react-form';
import { t } from 'i18next';
import type z from 'zod';
import { useHandlers } from '../../../../../context/page-context';
import type { PriceListTemplate } from '../../../../price-lists';
import { PREVIEW_PLACEHOLDER_DISCOUNT } from './constants';
import { discountFormSchema } from './schemas';
import { useCreateTemplatePriceList } from './use-create-template-price-list';

function seedAmount(template: PriceListTemplate | undefined): string {
  return String(template?.defaultDiscount ?? PREVIEW_PLACEHOLDER_DISCOUNT);
}

/**
 * Owns the one-click form: a TanStack Form validated by {@link discountFormSchema} whose submit
 * creates the template price list. Field errors surface inline; a create failure is toasted instead.
 */
export function useDiscountPriceListForm(template: PriceListTemplate | undefined) {
  const { notificationHandler } = useHandlers();
  const create = useCreateTemplatePriceList();

  return useForm({
    // z.input, not z.output: defaultValues hold the raw string the user edits, before the schema
    // transforms it to a number on submit.
    defaultValues: { discount: seedAmount(template) } satisfies z.input<typeof discountFormSchema>,
    validators: { onChange: discountFormSchema },
    onSubmit: async ({ value }) => {
      const { discount } = discountFormSchema.parse(value);
      try {
        await create.mutateAsync(discount);
      } catch {
        notificationHandler.notify(
          t('notifications:titles.error'),
          t('features:onboarding.components.price_list_item.error_generic'),
          'error',
        );
      }
    },
  });
}
